import { StyleSheet, View } from '../../compiler'
import { colors, typoStyles } from '../../constants'
import { useTranslationContext } from '../../context/general-translations'
import MarkdownText from '../MarkdownText'
import CultureTopThree from './CultureTopThree'
import DimensionTitle from './DimensionTitle'
import {
  dimensionId,
  MAX_RANK,
  pad2,
  selectTopDimensions,
  toRank
} from './rank'

/**
 * Werte-Rangliste im Culture "long mode", geteilt zwischen Anforderungs- und
 * Talentprofil. Portiert aus aivy-pdf-reports (src/reports_v2/components/
 * culture/CultureRankedList.js), Auswahl, Sortierung und Deckelung unverändert.
 *
 * Aufbau ist Top-3-Bande (ohne Kopfzeile), optionaler Spaltenkopf, Rangliste.
 * Die Zeilen beginnen nach den Rängen, die die Bande zeigt, in der Regel bei
 * 04. Eine Rangskala gibt es bewusst nicht, bei Rangsortierung trüge sie keine
 * Information, die nicht schon im Badge steht.
 *
 * - withColumnHeader nutzt nur das Talentprofil ("Rang" über den Badges).
 * - withTopThree={false} nutzt der Anhang des Talentprofils, dort steht die
 *   vollständige Liste ab 01.
 *
 * Abweichungen fürs Web (nur Dashboard):
 * - keine festen Zeilenhöhen und kein maxLines, das war Seitenbudget für
 *   wrap={false}. Texte laufen vollständig.
 * - onClickDimension macht jeden Wert klickbar (Info-Icon wie DimensionCard).
 *   Der Host öffnet damit den Assistenten. Werte aus der Bande werden vorher
 *   über ihren key in ranked_dimensions nachgeschlagen, weil top_dimensions
 *   weder description noch detail mitliefert.
 * - id reicht ein Joyride-Ziel an die Karte durch.
 *
 * Short mode rendern die Profile weiterhin selbst.
 */

const BORDER_COLOR = colors.lightGrey

const BADGE = 40
const COL_GAP = 24
const PAD_H = 24

const COL_HEADER_H = 56
const ROW_H = 88

const COLUMN_LABEL_KEYS = [
  'culture_rank_rank_column',
  'culture_rank_value_column'
]

const styles = StyleSheet.create({
  /* overflow clippt die Top-3-Bande auf die 8px-Rundung, sie ist erstes Kind
     und ihr rechteckiger Hintergrund liefe sonst über die obere Ecke */
  card: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: BORDER_COLOR,
    overflow: 'hidden'
  },

  /* Spaltenkopf, gleiches Skelett wie die Zeilen, damit "Rang" exakt über den
     Badges steht */
  colHeaderRow: {
    minHeight: COL_HEADER_H,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: COL_GAP,
    paddingHorizontal: PAD_H,
    borderBottomWidth: 1,
    borderColor: BORDER_COLOR
  },
  /* flexDirection column, weil der Shim sonst row setzt und alignItems dann
     vertikal statt horizontal zentriert */
  colHeaderBadgeCell: {
    width: BADGE,
    flexShrink: 0,
    flexDirection: 'column',
    alignItems: 'center'
  },
  colLabel: {
    ...typoStyles.SmallBlack,
    textTransform: 'uppercase',
    color: colors.rockGrey
  },

  row: {
    minHeight: ROW_H,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: COL_GAP,
    paddingHorizontal: PAD_H,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderColor: BORDER_COLOR
  },
  badge: {
    width: BADGE,
    height: BADGE,
    borderRadius: BADGE / 2,
    borderWidth: 2,
    borderColor: colors.primaryBlue,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  /* alignItems flex-start, sonst streckt die Flex-Spalte den Titel-Button
     über die ganze Zeile und die leere Fläche daneben wäre klickbar */
  titleCell: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minWidth: 0
  },
  rowTitle: typoStyles.H4Black,
  rowDescription: {
    ...typoStyles.T1Black,
    color: colors.rockGrey,
    marginTop: 4,
    display: 'block'
  }
})

const ValueRow = ({
  item,
  isLast,
  onClickDimension,
  hasCompletedOnboarding
}) => (
  <View style={[styles.row, isLast && { borderBottomWidth: 0 }]}>
    <View style={styles.badge}>
      <MarkdownText style={typoStyles.T1Blue}>{pad2(item.rank)}</MarkdownText>
    </View>
    <View style={styles.titleCell}>
      <DimensionTitle
        title={item.title}
        style={styles.rowTitle}
        onClick={onClickDimension ? () => onClickDimension(item) : undefined}
        hasCompletedOnboarding={hasCompletedOnboarding}
      />
      {!!item.description && (
        <MarkdownText style={styles.rowDescription}>
          {item.description}
        </MarkdownText>
      )}
    </View>
  </View>
)

const CultureRankedList = ({
  id,
  containerStyle = {},
  data = [],
  topDimensions,
  withTopThree = true,
  withColumnHeader = false,
  onClickDimension,
  hasCompletedOnboarding = true
}) => {
  const { tGeneral } = useTranslationContext()

  /* Reihenfolge nie dem Payload überlassen. Aufsteigend nach Rang, das Badge
     ist die einzige Ordnungsinformation der Karte */
  const dimensions = (Array.isArray(data) ? data : [])
    .map((item) => ({
      ...item,
      rank: toRank(item?.rank),
      title: item?.title || item?.key || ''
    }))
    .filter(({ rank }) => rank !== null)
    .sort((a, b) => a.rank - b.rank)

  if (!dimensions.length) return null

  /* Dieselbe Auswahl, die die Bande rendert, fällt hier weg. Rendert die Bande
     nicht (withTopThree={false}, alter Payload ohne top_dimensions oder ein
     Eintrag ohne bullets), ist die Menge leer und die Liste bleibt
     vollständig. */
  const topThree = withTopThree ? selectTopDimensions(topDimensions?.data) : []
  const shownAbove = new Set(topThree.map(dimensionId))

  /* toRank begrenzt die Ränge auf 1..10, nicht die Zeilenzahl. Doppelte Ränge
     im Payload ergäben mehr als zehn Zeilen, gedeckelt wird deshalb die
     Vereinigung aus Bande und Liste. */
  const rows = dimensions
    .filter((item) => !shownAbove.has(dimensionId(item)))
    .slice(0, MAX_RANK - topThree.length)

  /* tGeneral gibt im Dashboard bei fehlendem Key den Key selbst zurück, im PDF
     undefined. Ältere Payloads ohne die Keys verlieren deshalb die Kopfzeile,
     statt den Rohkey anzuzeigen. Stehen alle Ränge schon in der Bande, gäbe es
     unter dem Kopf keine Zeile mehr, dann entfällt er ebenfalls. */
  const [rankLabel, valueLabel] = COLUMN_LABEL_KEYS.map((key) => {
    const label = tGeneral(key)
    return label && label !== key ? label : null
  })
  const showColumnHeader =
    withColumnHeader && !!rankLabel && !!valueLabel && rows.length > 0

  const byId = new Map(dimensions.map((item) => [dimensionId(item), item]))
  const onClickTopDimension = onClickDimension
    ? (item) => onClickDimension(byId.get(dimensionId(item)) || item)
    : undefined

  return (
    <View id={id} style={{ ...styles.card, ...containerStyle }}>
      {withTopThree && (
        <CultureTopThree
          {...topDimensions}
          withHeader={false}
          withBottomBorder={rows.length > 0}
          onClickDimension={onClickTopDimension}
          hasCompletedOnboarding={hasCompletedOnboarding}
        />
      )}

      {showColumnHeader && (
        <View style={styles.colHeaderRow}>
          <View style={styles.colHeaderBadgeCell}>
            <MarkdownText style={styles.colLabel}>{rankLabel}</MarkdownText>
          </View>
          <View style={styles.titleCell}>
            <MarkdownText style={styles.colLabel}>{valueLabel}</MarkdownText>
          </View>
        </View>
      )}

      {rows.map((item, index) => (
        <ValueRow
          key={item.key || index}
          item={item}
          isLast={index === rows.length - 1}
          onClickDimension={onClickDimension}
          hasCompletedOnboarding={hasCompletedOnboarding}
        />
      ))}
    </View>
  )
}

export default CultureRankedList
